import { PartnerOfferDetails } from '@varo/graphql-schema-types';
import rewardFeaturedCardTextureBgDefault from 'assets/images/vxl/textures/reward-featured-card-texture-bg-default.png';
import DynamicMapCard from 'features/rewards/components/MapCard/DynamicMapCard';
import TitleLockUp from 'features/rewards/components/TitleLockUp/TitleLockUp';
import OfferDetailsHeader from 'features/rewards/Details/OfferDetailsHeader/OfferDetailsHeader';
import { buildLocalOfferLocation } from 'features/rewards/helpers/buildLocalOfferLocationFromOfferDetails.helpers';
import { openMap } from 'features/rewards/helpers/openMap.helpers';
import React from 'react';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const MerchantDetailsV2 = (offer: PartnerOfferDetails) => {
  const localOfferLocation = buildLocalOfferLocation(offer);

  const hasCoordinateNumbers = (
    latitude: number | undefined,
    longitude: number | undefined,
  ) => {
    return typeof latitude === 'number' && typeof longitude === 'number';
  };
  const hasCenter = hasCoordinateNumbers(
    offer.mapData?.center?.latitude,
    offer.mapData?.center?.longitude,
  );
  const hasCoord = hasCoordinateNumbers(
    offer.coordinates?.latitude,
    offer.coordinates?.longitude,
  );
  const hasLocal = hasCoordinateNumbers(
    localOfferLocation?.coordinate?.latitude,
    localOfferLocation?.coordinate?.longitude,
  );

  const centerLocation = (() => {
    if (hasCenter) {
      return {
        lat: offer.mapData!.center.latitude,
        lng: offer.mapData!.center.longitude,
      };
    }
    if (hasCoord && offer.coordinates) {
      return {
        lat: offer.coordinates.latitude,
        lng: offer.coordinates.longitude,
      };
    }
    if (hasLocal) {
      return {
        lat: localOfferLocation!.coordinate!.latitude,
        lng: localOfferLocation!.coordinate!.longitude,
      };
    }
    return undefined;
  })();

  return (
    <>
      {!centerLocation ? (
        <OfferDetailsHeader
          defaultHeroImage={
            rewardFeaturedCardTextureBgDefault as ImageSourcePropType
          }
          heroImage={offer.heroImage}
          logoImage={undefined}
        />
      ) : null}
      <TitleLockUp
        merchant={offer.name || ''}
        offerRate={offer.cashbackDescription || ''}
        logoUrl={offer.logoImage}
        tag={offer.tag}
      />
      {centerLocation ? (
        <DynamicMapCard
          id={offer.id}
          markerData={localOfferLocation ? [localOfferLocation] : []}
          cameraLocation={centerLocation}
          onMarkerPress={() => {}}
          onMapCardPress={() => openMap(centerLocation.lat, centerLocation.lng)}
          allowMapInteractions={false}
          usePillAnnotations={false}
          containerStyle={styles.mapContainer}
        />
      ) : null}
      {offer.redemptionLocationInfo ? (
        <ListItem
          showsChevron={false}
          primaryCopy={offer.redemptionLocationInfo?.text ?? ''}
          leadingAccessory={{
            id: '',
            name: VXLComponentId.ICON,
            props: {
              iconKey: offer.redemptionLocationInfo?.icon,
              iconSize: 'MD',
            },
          }}
          trailingAccessory={{
            id: '1',
            name: VXLComponentId.LIST_ITEM_TEXT_STACK,
            props: {
              primaryCopy: offer.redemptionLocationInfo?.trailingText ?? '',
              primaryCopyColor: theme.color.text.secondary,
            },
          }}
        />
      ) : null}
      {offer.cardUsageInfo ? (
        <>
          {offer.redemptionLocationInfo ? (
            <View style={styles.hairLine}>
              <HairLineDivider />
            </View>
          ) : null}
          <ListItem
            showsChevron={false}
            primaryCopy={offer.cardUsageInfo?.text ?? ''}
            leadingAccessory={{
              id: '',
              name: VXLComponentId.ICON,
              props: {
                iconKey: offer.cardUsageInfo?.icon,
                iconSize: 'MD',
              },
            }}
            trailingAccessory={{
              id: '1',
              name: VXLComponentId.LIST_ITEM_TEXT_STACK,
              props: {
                primaryCopy: offer.cardUsageInfo?.trailingText ?? '',
                primaryCopyColor: theme.color.text.secondary,
              },
            }}
          />
        </>
      ) : null}
      {offer.maxEarningsInfo ? (
        <>
          {offer.cardUsageInfo || offer.redemptionLocationInfo ? (
            <View style={styles.hairLine}>
              <HairLineDivider />
            </View>
          ) : null}
          <ListItem
            showsChevron={false}
            primaryCopy={offer.maxEarningsInfo?.text ?? ''}
            leadingAccessory={{
              id: '',
              name: VXLComponentId.ICON,
              props: {
                iconKey: offer.maxEarningsInfo?.icon,
                iconSize: 'MD',
              },
            }}
            trailingAccessory={{
              id: '1',
              name: VXLComponentId.LIST_ITEM_TEXT_STACK,
              props: {
                primaryCopy: offer.maxEarningsInfo?.trailingText ?? '',
                primaryCopyColor: theme.color.text.secondary,
              },
            }}
          />
        </>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  hairLine: {
    marginHorizontal: theme.spacing.m,
  },
  mapContainer: {
    height: 120,
    paddingHorizontal: theme.spacing.m,
    marginBottom: theme.spacing.s,
  },
});

export default MerchantDetailsV2;
